import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Sparkles, User } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { createPatient } from '@/utils/api';
import { toast } from 'sonner';

export default function Onboarding({ onComplete }) {
  const [formData, setFormData] = useState({
    nome: '',
    cognome: '',
    data_nascita: '',
  });
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await createPatient(formData);
      toast.success('Profilo creato con successo!');
      onComplete(response.data);
    } catch (error) {
      console.error('Errore nella creazione del profilo:', error);
      toast.error('Errore nella creazione del profilo');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.5 }}
        className="w-full max-w-md"
      >
        <div className="text-center mb-8">
          <motion.div
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ delay: 0.2, type: 'spring' }}
            className="w-20 h-20 rounded-3xl bg-gradient-to-br from-blue-500 to-blue-600 flex items-center justify-center mx-auto mb-4 soft-shadow-lg"
          >
            <Sparkles className="w-10 h-10 text-white" />
          </motion.div>
          <h1 className="text-4xl font-bold text-slate-900 mb-2">ParkMio Pro</h1>
          <p className="text-lg text-slate-600">Benvenuto nel tuo assistente personale</p>
        </div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3, duration: 0.4 }}
          className="bg-white rounded-3xl p-8 soft-shadow border border-slate-200/60"
        >
          <div className="flex items-center gap-3 mb-6">
            <div className="w-12 h-12 rounded-2xl bg-blue-100 flex items-center justify-center">
              <User className="w-6 h-6 text-blue-600" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-slate-900">Crea il tuo profilo</h2>
              <p className="text-sm text-slate-600">Iniziamo con le informazioni di base</p>
            </div>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <Label htmlFor="nome" className="text-base font-medium text-slate-700 mb-2 block">
                Nome
              </Label>
              <Input
                id="nome"
                data-testid="input-nome"
                type="text"
                required
                value={formData.nome}
                onChange={(e) => setFormData({ ...formData, nome: e.target.value })}
                className="h-16 text-lg rounded-2xl border-slate-300 focus:border-blue-500"
                placeholder="Il tuo nome"
              />
            </div>

            <div>
              <Label htmlFor="cognome" className="text-base font-medium text-slate-700 mb-2 block">
                Cognome
              </Label>
              <Input
                id="cognome"
                data-testid="input-cognome"
                type="text"
                required
                value={formData.cognome}
                onChange={(e) => setFormData({ ...formData, cognome: e.target.value })}
                className="h-16 text-lg rounded-2xl border-slate-300 focus:border-blue-500"
                placeholder="Il tuo cognome"
              />
            </div>

            <div>
              <Label htmlFor="data_nascita" className="text-base font-medium text-slate-700 mb-2 block">
                Data di Nascita
              </Label>
              <Input
                id="data_nascita"
                data-testid="input-data-nascita"
                type="date"
                required
                value={formData.data_nascita}
                onChange={(e) => setFormData({ ...formData, data_nascita: e.target.value })}
                className="h-16 text-lg rounded-2xl border-slate-300 focus:border-blue-500"
              />
            </div>

            <Button
              type="submit"
              data-testid="btn-crea-profilo"
              disabled={loading}
              className="w-full min-h-[64px] text-lg font-semibold rounded-2xl bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white shadow-lg hover:shadow-xl transition-all duration-300 active:scale-[0.98]"
            >
              {loading ? 'Creazione in corso...' : 'Inizia il Monitoraggio'}
            </Button>
          </form>
        </motion.div>
      </motion.div>
    </div>
  );
}
